import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AddressPayload, BankAccountPayload, ContactKind, EmergencyPayload, Owner, aprovarEndereco, contaPrincipal, criarConta,
  criarEndereco, encerrarConta, filaDeValidacao, listarBancos, listarContas, listarContatos, listarEnderecos,
  rejeitarConta, rejeitarEndereco, removerContato, removerContatoEmergencia, salvarContato, salvarContatoEmergencia,
  validarConta,
} from '../api/cadastro';
import {
  DocumentTypePayload, ReviewAction, UploadPayload, analisarDocumento, cancelarDocumento, checklistDoVinculo,
  enviarDocumento, enviarMeuDocumento, listarDocumentos, listarTiposDocumentais, meuChecklist, meusDocumentos,
  meusTiposDocumentais, salvarTipoDocumental, situacaoTipoDocumental, versoesDocumento,
} from '../api/documentos';
import { PESSOAS_KEYS } from './index';

const ownerKey = (owner: Owner) => (owner.kind === 'me' ? 'me' : owner.personId);

export const CADASTRO_KEYS = {
  tipos: (active: string) => ['pessoas', 'tipos-documentais', active] as const,
  documentos: (personId: string, history: boolean) => ['pessoas', 'documentos', personId, history] as const,
  checklist: (bondId: string) => ['pessoas', 'checklist', bondId] as const,
  versoes: (docId: string) => ['pessoas', 'documentos', 'versoes', docId] as const,
  enderecos: (owner: Owner) => ['pessoas', 'enderecos', ownerKey(owner)] as const,
  contatos: (owner: Owner) => ['pessoas', 'contatos', ownerKey(owner)] as const,
  bancos: ['pessoas', 'bancos'] as const,
  contas: (owner: Owner) => ['pessoas', 'contas', ownerKey(owner)] as const,
  fila: ['pessoas', 'fila'] as const,
  meusDocumentos: ['pessoas', 'me', 'documentos'] as const,
  meuChecklist: ['pessoas', 'me', 'checklist'] as const,
  meusTipos: ['pessoas', 'me', 'tipos'] as const,
};

/** Toda escrita invalida o módulo de pessoas (detalhe, pendências, fila e portal mudam juntos). */
function useWrite<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: PESSOAS_KEYS.all }) });
}

// --- Documentos ----------------------------------------------------

export const useTiposDocumentais = (active: 'true' | 'false' | 'all' = 'true', enabled = true) =>
  useQuery({ queryKey: CADASTRO_KEYS.tipos(active), queryFn: () => listarTiposDocumentais(active), enabled, staleTime: 60_000 });

export const useSalvarTipoDocumental = () =>
  useWrite(({ id, payload }: { id: string | null; payload: Partial<DocumentTypePayload> }) => salvarTipoDocumental(id, payload));

export const useSituacaoTipoDocumental = () =>
  useWrite(({ id, active }: { id: string; active: boolean }) => situacaoTipoDocumental(id, active));

export const useDocumentos = (personId: string, history: boolean, enabled = true) =>
  useQuery({ queryKey: CADASTRO_KEYS.documentos(personId, history), queryFn: () => listarDocumentos(personId, history), enabled });

export const useChecklist = (bondId: string | undefined) =>
  useQuery({ queryKey: CADASTRO_KEYS.checklist(bondId ?? ''), queryFn: () => checklistDoVinculo(bondId as string), enabled: !!bondId });

export const useVersoes = (docId: string | null) =>
  useQuery({ queryKey: CADASTRO_KEYS.versoes(docId ?? ''), queryFn: () => versoesDocumento(docId as string), enabled: !!docId });

export const useEnviarDocumento = (personId: string) => useWrite((payload: UploadPayload) => enviarDocumento(personId, payload));

export const useAnalisarDocumento = () =>
  useWrite(({ id, action, reason, comment }: { id: string; action: ReviewAction; reason?: string; comment?: string }) =>
    analisarDocumento(id, action, reason, comment));

export const useCancelarDocumento = () =>
  useWrite(({ id, justification }: { id: string; justification: string }) => cancelarDocumento(id, justification));

export const useMeusDocumentos = () => useQuery({ queryKey: CADASTRO_KEYS.meusDocumentos, queryFn: meusDocumentos });
export const useMeuChecklist = () => useQuery({ queryKey: CADASTRO_KEYS.meuChecklist, queryFn: meuChecklist });
export const useMeusTipos = () => useQuery({ queryKey: CADASTRO_KEYS.meusTipos, queryFn: meusTiposDocumentais, staleTime: 60_000 });
export const useEnviarMeuDocumento = () => useWrite((payload: UploadPayload) => enviarMeuDocumento(payload));

// --- Endereços e contatos ------------------------------------------

export const useEnderecos = (owner: Owner, enabled = true) =>
  useQuery({ queryKey: CADASTRO_KEYS.enderecos(owner), queryFn: () => listarEnderecos(owner), enabled });

export const useCriarEndereco = (owner: Owner) => useWrite((payload: AddressPayload) => criarEndereco(owner, payload));
export const useAprovarEndereco = () => useWrite((id: string) => aprovarEndereco(id));
export const useRejeitarEndereco = () => useWrite(({ id, reason }: { id: string; reason: string }) => rejeitarEndereco(id, reason));

export const useContatos = (owner: Owner, enabled = true) =>
  useQuery({ queryKey: CADASTRO_KEYS.contatos(owner), queryFn: () => listarContatos(owner), enabled });

export const useSalvarContato = (owner: Owner) =>
  useWrite(({ id, payload }: { id: string | null; payload: { kind: ContactKind; value: string; isPrimary?: boolean } }) =>
    salvarContato(owner, id, payload));
export const useRemoverContato = (owner: Owner) => useWrite((id: string) => removerContato(owner, id));
export const useSalvarEmergencia = (owner: Owner) =>
  useWrite(({ id, payload }: { id: string | null; payload: EmergencyPayload }) => salvarContatoEmergencia(owner, id, payload));
export const useRemoverEmergencia = (owner: Owner) => useWrite((id: string) => removerContatoEmergencia(owner, id));

// --- Contas bancárias ----------------------------------------------

export const useBancos = (owner: Owner, enabled = true) =>
  useQuery({ queryKey: CADASTRO_KEYS.bancos, queryFn: () => listarBancos(owner), enabled, staleTime: Infinity });

export const useContas = (owner: Owner, enabled = true) =>
  useQuery({ queryKey: CADASTRO_KEYS.contas(owner), queryFn: () => listarContas(owner), enabled });

export const useCriarConta = (owner: Owner) => useWrite((payload: BankAccountPayload) => criarConta(owner, payload));
export const useValidarConta = () => useWrite(({ id, note }: { id: string; note?: string }) => validarConta(id, note));
export const useRejeitarConta = () => useWrite(({ id, reason }: { id: string; reason: string }) => rejeitarConta(id, reason));
export const useContaPrincipal = () => useWrite((id: string) => contaPrincipal(id));
export const useEncerrarConta = () => useWrite((id: string) => encerrarConta(id));

// --- Fila ----------------------------------------------------------

export const useFilaDeValidacao = () => useQuery({ queryKey: CADASTRO_KEYS.fila, queryFn: filaDeValidacao });
